/**
 * Catalogue of the third-party systems Trinetra can sit alongside.
 *
 * FRONT-END ONLY, ON PURPOSE. Nothing here talks to a server: the page exists so a
 * customer can see what integrating their existing VMS or analytics stack will look
 * like before any connector is built. Every "connect", "test" and "import" below is
 * staged in component state and forgotten on reload. When the real connector service
 * lands, this file becomes the shape the API returns and the panels keep their layout.
 */

export type ConnectorKind = 'vms' | 'analytics';

export interface Connector {
  id: string;
  name: string;
  /** Who makes it — rendered under the name so an operator recognises their own stack. */
  vendor: string;
  kind: ConnectorKind;
  /** The transport the real connector would speak. Shown as a chip on the card. */
  protocol: string;
  /** Default port pre-filled in the connection form. */
  defaultPort: number;
  /** One line an operator can match against what they already run. */
  summary: string;
  /** What Trinetra would pull from (or push to) this system. */
  capabilities: string[];
}

/** Two letters for the card tile — vendor logos are not ours to ship. */
export function connectorInitials(name: string): string {
  const words = name.split(/[\s-]+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export const VMS_CONNECTORS: Connector[] = [
  {
    id: 'milestone',
    name: 'Milestone XProtect',
    vendor: 'Milestone Systems',
    kind: 'vms',
    protocol: 'MIP REST + RTSP',
    defaultPort: 443,
    summary: 'Pull the camera tree, live streams and recorded sequences from an XProtect management server.',
    capabilities: ['Cameras & streams', 'PTZ control', 'Recording index', 'Alarms'],
  },
  {
    id: 'genetec',
    name: 'Genetec Security Center',
    vendor: 'Genetec',
    kind: 'vms',
    protocol: 'Web SDK (REST)',
    defaultPort: 4590,
    summary: 'Read the Omnicast camera inventory and raise Trinetra detections as Security Center events.',
    capabilities: ['Cameras & streams', 'Events in/out', 'Access control doors'],
  },
  {
    id: 'hikcentral',
    name: 'HikCentral Professional',
    vendor: 'Hikvision',
    kind: 'vms',
    protocol: 'OpenAPI (HTTP)',
    defaultPort: 443,
    summary: 'Import the resource list from HikCentral and reuse its stream URLs without re-cabling cameras.',
    capabilities: ['Cameras & streams', 'Recording index', 'Events in/out'],
  },
  {
    id: 'dahua-dss',
    name: 'Dahua DSS Pro',
    vendor: 'Dahua',
    kind: 'vms',
    protocol: 'DSS OpenAPI',
    defaultPort: 443,
    summary: 'Sync devices and channels from a DSS Pro deployment and mirror its groupings as Trinetra groups.',
    capabilities: ['Cameras & streams', 'Device groups', 'Events in/out'],
  },
  {
    id: 'bosch-bvms',
    name: 'Bosch BVMS',
    vendor: 'Bosch',
    kind: 'vms',
    protocol: 'Central Server SDK',
    defaultPort: 5322,
    summary: 'Read the BVMS configuration and attach Trinetra analytics to cameras already managed there.',
    capabilities: ['Cameras & streams', 'PTZ control', 'Alarms'],
  },
  {
    id: 'avigilon',
    name: 'Avigilon Control Center',
    vendor: 'Motorola Solutions',
    kind: 'vms',
    protocol: 'ACC Web Endpoint',
    defaultPort: 8443,
    summary: 'Enumerate ACC cameras and hand their streams to Trinetra pipelines for AI processing.',
    capabilities: ['Cameras & streams', 'Recording index'],
  },
  {
    id: 'axis-acs',
    name: 'Axis Camera Station',
    vendor: 'Axis Communications',
    kind: 'vms',
    protocol: 'ACS REST + VAPIX',
    defaultPort: 55756,
    summary: 'Adopt an Axis Camera Station site, including per-camera VAPIX settings and recording profiles.',
    capabilities: ['Cameras & streams', 'PTZ control', 'Recording index'],
  },
  {
    id: 'onvif-generic',
    name: 'Generic ONVIF',
    vendor: 'Any ONVIF Profile S / T device',
    kind: 'vms',
    protocol: 'ONVIF + RTSP',
    defaultPort: 80,
    summary: 'No VMS at all — point Trinetra at an ONVIF recorder or NVR and discover its channels directly.',
    capabilities: ['Cameras & streams', 'PTZ control', 'Device discovery'],
  },
  {
    id: 'rest-generic',
    name: 'Custom REST / RTSP',
    vendor: 'In-house or unlisted system',
    kind: 'vms',
    protocol: 'HTTP + RTSP',
    defaultPort: 443,
    summary: 'Anything else: describe an endpoint that returns a camera list and Trinetra maps it onto its own model.',
    capabilities: ['Cameras & streams', 'Scheduled re-sync'],
  },
];

export const ANALYTICS_CONNECTORS: Connector[] = [
  {
    id: 'anpr-third-party',
    name: 'Third-party ANPR',
    vendor: 'Plate recognition engine',
    kind: 'analytics',
    protocol: 'Webhook / gRPC',
    defaultPort: 443,
    summary: 'Keep an existing plate-reading engine and let its reads land in the Trinetra event timeline.',
    capabilities: ['Plate reads in', 'Watch-list hits', 'Snapshots'],
  },
  {
    id: 'face-third-party',
    name: 'External face engine',
    vendor: 'Face recognition vendor',
    kind: 'analytics',
    protocol: 'REST + webhook',
    defaultPort: 443,
    summary: 'Forward frames to an external matcher and show its identities beside Trinetra detections.',
    capabilities: ['Identity matches in', 'Enrolment sync'],
  },
  {
    id: 'fire-smoke',
    name: 'Fire & smoke detection',
    vendor: 'Specialist safety model',
    kind: 'analytics',
    protocol: 'Webhook',
    defaultPort: 443,
    summary: 'Raise a Trinetra alert whenever a dedicated fire/smoke model fires on a shared stream.',
    capabilities: ['Alerts in', 'Camera mapping'],
  },
  {
    id: 'ppe-safety',
    name: 'PPE / safety compliance',
    vendor: 'Industrial safety analytics',
    kind: 'analytics',
    protocol: 'Webhook',
    defaultPort: 443,
    summary: 'Ingest helmet, vest and exclusion-zone violations as first-class Trinetra events.',
    capabilities: ['Alerts in', 'Zone mapping'],
  },
  {
    id: 'bi-export',
    name: 'BI / data warehouse',
    vendor: 'Reporting stack',
    kind: 'analytics',
    protocol: 'Outbound webhook',
    defaultPort: 443,
    summary: 'Stream Trinetra counts and events outwards so existing dashboards keep working.',
    capabilities: ['Events out', 'Counts out', 'Scheduled batches'],
  },
];

/** Connection state a card shows. `connected` here means "staged in this preview". */
export type ConnectionStatus = 'idle' | 'testing' | 'connected' | 'error';

export interface ConnectionState {
  status: ConnectionStatus;
  host: string;
  port: string;
  username: string;
  password: string;
  verifyTls: boolean;
  syncInterval: string;
  /** Last simulated test result, rendered under the form. */
  message?: string;
  testedAt?: string;
}

export function blankConnection(c: Connector): ConnectionState {
  return {
    status: 'idle',
    host: '',
    port: String(c.defaultPort),
    username: '',
    password: '',
    verifyTls: true,
    syncInterval: '15m',
  };
}
